import { useDispatch } from 'react-redux';
import * as Sentry from '@sentry/react';
import {
  TApiApplicationDocument,
  TLocalFiles,
  TApplicationStatus,
} from '~/modules/covenants/types.ts';
import { AppDispatch } from '~/store/store.ts';
import {
  fetchApplicationUnsignedDocumentsCount,
  requestApplicationDocumentsPage,
} from '~/store/covenants/thunks/applicationThunks.ts';
import { SIGNING_DOCUMENTS_PREFETCH_MIN_PAGE_SIZE } from '~/modules/covenants/CovenantsDataApplicationForm/constants.ts';
import { hasAnyApplicationFiles } from '~/modules/covenants/CovenantsDataApplicationForm/utils.ts';
import { buildApplicationDocumentRowKey } from '~/modules/covenants/utils.ts';

type TUseBulkSignClickParams = {
  isSignatureInteractionBlocked: boolean;
  isSubmitting: boolean;
  applicationId?: string;
  contragentId: number;
  applicationStatus?: TApplicationStatus;
  documentRows: TApiApplicationDocument[];
  documentsRowsPerPage: number;
  localFiles: TLocalFiles;
  onSignatureFlowLoadingChange: (isLoading: boolean) => void;
  onSignatureFlowDocumentsChange: (documents: TApiApplicationDocument[]) => void;
  onNoFiles: () => void;
  onNoUnsignedDocuments: () => Promise<void> | void;
  onOpenSignatureModal: () => void;
};

export const useBulkSignClick = ({
  isSignatureInteractionBlocked,
  isSubmitting,
  applicationId,
  contragentId,
  applicationStatus,
  documentRows,
  documentsRowsPerPage,
  localFiles,
  onSignatureFlowLoadingChange,
  onSignatureFlowDocumentsChange,
  onNoFiles,
  onNoUnsignedDocuments,
  onOpenSignatureModal,
}: TUseBulkSignClickParams) => {
  const dispatch = useDispatch<AppDispatch>();

  const loadAllDocumentsForSigning = async (): Promise<TApiApplicationDocument[]> => {
    if (!applicationId) {
      return documentRows;
    }

    const pageSize = Math.max(documentsRowsPerPage, SIGNING_DOCUMENTS_PREFETCH_MIN_PAGE_SIZE);
    const firstPage = await requestApplicationDocumentsPage({
      contragentId,
      applicationId,
      page: 0,
      size: pageSize,
      status: applicationStatus,
    });

    const totalElements = firstPage.pageConfiguration.totalElements ?? firstPage.count ?? 0;
    const totalPages = Math.max(1, Math.ceil(totalElements / pageSize));

    const remainingPages =
      totalPages > 1
        ? await Promise.all(
            Array.from({ length: totalPages - 1 }, (_, index) =>
              requestApplicationDocumentsPage({
                contragentId,
                applicationId,
                page: index + 1,
                size: pageSize,
                status: applicationStatus,
              })
            )
          )
        : [];

    const mergedDocuments = new Map<string, TApiApplicationDocument>();
    [firstPage, ...remainingPages].forEach((pageResponse) => {
      pageResponse.applicationDocuments.forEach((document) => {
        mergedDocuments.set(buildApplicationDocumentRowKey(document), document);
      });
    });

    return Array.from(mergedDocuments.values());
  };

  const handleBulkSignClick = async ({
    skipUnsignedCountFetch = false,
  }: {
    skipUnsignedCountFetch?: boolean;
  } = {}) => {
    if (isSignatureInteractionBlocked || isSubmitting) {
      return;
    }

    let nextSignatureFlowDocuments = documentRows;
    let unsignedDocumentsCount: number | null = null;

    if (applicationId && contragentId) {
      onSignatureFlowLoadingChange(true);
      try {
        nextSignatureFlowDocuments = await loadAllDocumentsForSigning();
        onSignatureFlowDocumentsChange(nextSignatureFlowDocuments);

        if (!skipUnsignedCountFetch) {
          unsignedDocumentsCount = await dispatch(
            fetchApplicationUnsignedDocumentsCount({
              contragentId,
              applicationId,
            })
          ).unwrap();
        }
      } catch (error) {
        Sentry.captureException(error);
      } finally {
        onSignatureFlowLoadingChange(false);
      }
    }

    const hasFilesForSigning = hasAnyApplicationFiles(nextSignatureFlowDocuments, localFiles);

    if (!hasFilesForSigning) {
      onNoFiles();
      return;
    }

    if (!skipUnsignedCountFetch && typeof unsignedDocumentsCount === 'number' && unsignedDocumentsCount === 0) {
      await onNoUnsignedDocuments();
      return;
    }

    onOpenSignatureModal();
  };

  return {
    handleBulkSignClick,
  };
};
